import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from "rxjs";
import { IFile } from "src/app/components/widgets/forms/interfaces/iFile";
import { Files } from "src/app/miscellaneous/files";
import { BreakpointObserver, BreakpointState, Breakpoints } from "@angular/cdk/layout";

export enum SortBy {
  ASC = "ASC",
  DESC = "DESC",
  NO_ORDER = "NO_ORDER",
}
/// MINI CARD FILES ///
/*
 * @example
 *  {
 *  "endpoint": "http://localhost:8084/api/associable/files/BANDI_DI_GARA_E_CONTRATTI",
 *  "model_id": 9,
 *  "template": "mini-card-files",
 *  "props": {
 *      "endpoint": "http://localhost:8084/api/files",
 *      "keyTitle": "fileName", ---> La chiave per recuperare il titolo del file.
 *      "label": "Documenti", --->  Opzionale. DEFAULT: "Allegati"
 *      "page": "test",
 *      "showAction": true --->  Opzionale. Mostra i pulsanti di download e preview. Default: FALSE.
 *      "viewTemplate": "grid", Opzionale. Template disponibili : 'carousel' , 'pagination', 'grid'. Default: 'grid'.
 *      "sortBy": "ASC" --->  Opzionale. Ordinamento dei file. Default: "ASC".
 *      "elementsToShow": 5 ---> Opzionale. Numero di elementi da visualizzare. MAX per mini card: 5.
 *   }
 *  }
 */

@Component({
  selector: "app-mini-card-files",
  templateUrl: "./mini-card-files.component.html",
  styleUrls: ["./mini-card-files.component.scss"],
})
export class MiniCardFilesComponent implements AfterViewInit, OnDestroy {
  /**
   * Data da Asset Publisher.
   */
  data!: any;

  /**
   * BehaviorSubject per la lista di file.
   */
  _files$ = new BehaviorSubject<IFile[]>([]);

  /**
   * Le props definite in fase di configurazione.
   */
  props: any;

  /**
   * Array che contiene la lista di file.
   */
  originalFileList!: IFile[];

  /**
   * Opzioni per il sort.
   */
  sortByOptions = [
    { value: SortBy.ASC, label: "Asc" },
    { value: SortBy.DESC, label: "Desc" },
    { value: SortBy.NO_ORDER, label: "Default" },
  ];

  /**
   * Ordinamento corrente.
   */
  sortBy = SortBy.NO_ORDER;

  /**
   * Template di visualizzazione.
   */
  viewTemplate!: "grid" | "carousel" | "pagination";

  /**
   * Pagina corrente.
   */
  page = 1;

  /**
   * Elementi nella pagina.
   */
  pageSize = 5;

  /**
   * File paginati.
   */
  paginatedFiles: IFile[] = [];

  /*
   *  The number of elements to show.
   */
  elementsToShow$ = new BehaviorSubject<number>(5);

  /**
   * Subject per l'OnDestroy del componente.
   */
  private destroy$ = new Subject<void>();

  constructor(
    private _breakPointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private file: Files
  ) {}

  /**
   * @inheritdoc
   */
  ngAfterViewInit(): void {
    if (this.data) {
      this.originalFileList = [...this.data.data];
      this._files$.next([...this.originalFileList]);
      this.props = this.data.props;
      this.checkBreakpoint();
      this.viewTemplate = this.props.viewTemplate || "grid";
      // For pagination.
      if (this.viewTemplate === "pagination") {
        this.setPaginatedFiles();
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Downloads file.
   */
  downloadFile(item: any) {
    this.file.download(this.props.endpoint, item.id, item.title);
  }

  /**
   * Previews file.
   */
  showPreview(item: any) {
    this.file.preview(this.props.endpoint, item.id);
  }

  /**
   * Returns title key from props.
   */
  getFileTitle(item: any): string {
    const key = this.props?.keyTitle;
    return item[key];
  }

  /**
   * it streams an observable it contains the filelist.
   */
  get files$(): Observable<IFile[]> {
    return this._files$.asObservable();
  }

  /**
   * Sort files by title.
   */
  sortFiles(selectedOrder: SortBy): void {
    this.sortBy = selectedOrder;

    const files = this._files$.getValue();
    const sortedFiles =
      this.sortBy !== SortBy.NO_ORDER
        ? files.sort((a, b) => {
            if (this.sortBy === SortBy.ASC) {
              return this.getFileTitle(a).localeCompare(this.getFileTitle(b));
            } else {
              return this.getFileTitle(b).localeCompare(this.getFileTitle(a));
            }
          })
        : [...this.originalFileList];

    this._files$.next([...sortedFiles]);
    this.viewTemplate === "pagination" && this.setPaginatedFiles();
  }

  /**
   * Set pagination.
   */
  setPaginatedFiles(): void {
    const files = this._files$.getValue();
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.paginatedFiles = files.slice(startIndex, endIndex);
  }

  /**
   * On page change event.
   */
  onPageChange(page: number): void {
    this.page = page;
    this.setPaginatedFiles();
  }

  /**
   * On page size change event.
   */
  onPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1; // Resetta la pagina corrente a 1
    this.setPaginatedFiles();
  }

  /**
   * Set number of visible item by breakpoints.
   */
  checkBreakpoint(): void {
    const managedSize = [
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ];

    this._breakPointObserver
      .observe(managedSize)
      .pipe(
        tap((state: BreakpointState) => {
          let elementsToShow = 5;
          if (state.breakpoints[Breakpoints.XSmall]) {
            elementsToShow = 1;
          } else if (state.breakpoints[Breakpoints.Small]) {
            elementsToShow = 2;
          } else if (state.breakpoints[Breakpoints.Medium]) {
            elementsToShow = 3;
          } else if (state.breakpoints[Breakpoints.Large]) {
            elementsToShow = 4;
          } else if (state.breakpoints[Breakpoints.XLarge]) {
            elementsToShow = 5;
          } 
          if (this.props?.elementsToShow && this.props.elementsToShow < elementsToShow) {
            elementsToShow = this.props.elementsToShow;
          }
          this.elementsToShow$.next(elementsToShow);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
